import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Search, 
  Clock, 
  DollarSign, 
  Pencil, 
  Info, 
  Trash2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  X,
  CheckCircle2
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import Modal from '../components/Modal';
import { intelligentSearch } from '../utils/searchUtils';
import './Studies.css';

const Studies = () => {
  const [studies, setStudies] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudy, setEditingStudy] = useState(null);
  const { addNotification } = useNotifications();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [servicesRes, areasRes] = await Promise.all([
        supabase.from('services').select('*').order('name'),
        supabase.from('professionals').select('id, full_name, services').order('full_name')
      ]);
      
      if (servicesRes.error) throw servicesRes.error;
      if (areasRes.error) throw areasRes.error;
      
      setStudies(servicesRes.data || []);
      setAreas(areasRes.data || []);
    } catch (err) {
      addNotification('Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchData();
    });
  }, []);

  const handleOpenNew = () => {
    setEditingStudy(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (study) => {
    setEditingStudy(study);
    setIsModalOpen(true);
  };

  const handleSaveStudy = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const studyData = {
      name: formData.get('name'),
      duration: parseInt(formData.get('duration')) || 15,
      price: parseFloat(formData.get('price')) || 0,
      requirements: formData.get('requirements') || ''
    };

    setLoading(true);
    try {
      let studyId;
      if (editingStudy) {
        const { error } = await supabase.from('services').update(studyData).eq('id', editingStudy.id);
        if (error) throw error;
        studyId = editingStudy.id;
      } else {
        const { data, error } = await supabase.from('services').insert([studyData]).select();
        if (error) throw error;
        studyId = data[0].id;
      }

      // Update areas assignment
      for (const area of areas) {
        const isChecked = formData.get(`area_${area.id}`) === 'on';
        const currentServices = area.services || [];
        const hasService = currentServices.includes(studyId);

        if (isChecked && !hasService) {
          const newServices = [...currentServices, studyId];
          await supabase.from('professionals').update({ services: newServices }).eq('id', area.id);
        } else if (!isChecked && hasService) {
          const newServices = currentServices.filter(id => id !== studyId);
          await supabase.from('professionals').update({ services: newServices }).eq('id', area.id);
        }
      }

      addNotification(`Práctica médica ${editingStudy ? 'actualizada' : 'agregada'}`, 'success');
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      addNotification('Error al guardar: ' + err.message, 'error');
      setLoading(false);
    }
  };

  const filteredStudies = studies.filter(s => 
    intelligentSearch(searchTerm, s.name)
  );

  return (
    <div className="studies-page">
      <div className="studies-header">
        <div className="studies-title-main">
          <div className="icon-wrap-s"><FileText size={28} /></div>
          <div>
            <h1>Estudios y Prácticas</h1>
            <p>Configuración del catálogo de servicios y prestaciones médicas.</p>
          </div>
        </div>
        <button className="btn-new-study premium-btn" onClick={handleOpenNew}>
          <Plus size={18} /> NUEVA PRÁCTICA
        </button>
      </div>

      <div className="studies-controls card glass-panel">
        <div className="search-bar-wrapper">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Buscar práctica por nombre..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn-refresh-s" onClick={fetchData}><RefreshCw size={18} /></button>
      </div>

      <div className="studies-table-container card glass-panel-premium">
        <table className="studies-table-v2">
          <thead>
            <tr>
              <th>Nombre de la Práctica</th>
              <th>Duración Estimada</th>
              <th>Valor Particular</th>
              <th className="text-center">Estado</th>
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="text-center-p">Sincronizando catálogo de servicios...</td></tr>
            ) : filteredStudies.length === 0 ? (
              <tr><td colSpan="5" className="text-center-p">No hay prácticas registradas en el catálogo.</td></tr>
            ) : filteredStudies.map(study => (
              <React.Fragment key={study.id}>
                <tr className={expandedId === study.id ? 'row-expanded' : ''}>
                  <td>
                    <div className="study-name-cell">
                      <CheckCircle2 size={16} className="check-icon" />
                      <span>{study.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="duration-pill">
                      <Clock size={14} /> {study.duration} min
                    </div>
                  </td>
                  <td className="price-cell">
                    $ {parseFloat(study.price).toLocaleString('es-AR')}
                  </td>
                  <td className="text-center">
                    <span className="status-badge-v2">HABILITADO</span>
                  </td>
                  <td className="text-center">
                    <div className="studies-actions-v2">
                      <button className="btn-icon-v2" onClick={() => setExpandedId(expandedId === study.id ? null : study.id)} title="Ver detalles">
                        {expandedId === study.id ? <ChevronUp size={18} /> : <Info size={18} />}
                      </button>
                      <button className="btn-icon-v2 edit" onClick={() => handleOpenEdit(study)} title="Editar práctica"><Pencil size={18} /></button>
                      <button className="btn-icon-v2 delete" onClick={async () => { if(window.confirm('¿Eliminar práctica?')) { await supabase.from('services').delete().eq('id', study.id); fetchData(); }}} title="Eliminar"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
                {expandedId === study.id && (
                  <tr className="study-detail-row-v2">
                    <td colSpan="5">
                      <div className="study-detail-expanded card glass-panel">
                        <h4>Detalles Técnicos</h4>
                        <p><strong>Requisitos:</strong> {study.requirements || 'No requiere preparación previa.'}</p>
                        <p><strong>ID de Sistema:</strong> {study.id}</p>
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong>Áreas asignadas: </strong>
                          {areas.filter(a => a.services?.includes(study.id)).map(a => a.full_name).join(', ') || 'Ninguna'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingStudy ? "Editar Práctica Médica" : "Nueva Práctica Médica"}>
        <form onSubmit={handleSaveStudy} className="study-form-premium">
          <div className="form-group-p">
            <label>Nombre del Estudio o Práctica</label>
            <input name="name" type="text" required defaultValue={editingStudy?.name || ''} placeholder="Ej: Ecografía Abdominal" />
          </div>
          <div className="form-grid-p">
            <div className="form-group-p">
              <label>Duración (Minutos)</label>
              <input name="duration" type="number" required defaultValue={editingStudy?.duration || 15} />
            </div>
            <div className="form-group-p">
              <label>Valor Particular ($)</label>
              <input name="price" type="number" step="0.01" required defaultValue={editingStudy?.price || ''} placeholder="0.00" />
            </div>
          </div>
          <div className="form-group-p">
            <label>Asignar a Áreas / Especialidades</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', background: 'rgba(0,0,0,0.2)', padding: '0.8rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
              {areas.map(area => (
                <label key={area.id} className="area-label">
                  <input className="area-checkbox" type="checkbox" name={`area_${area.id}`} defaultChecked={editingStudy ? area.services?.includes(editingStudy.id) : false} />
                  <span>{area.full_name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="form-group-p">
            <label>Requisitos / Preparación</label>
            <textarea name="requirements" rows="3" defaultValue={editingStudy?.requirements || ''} placeholder="Ej: Ayuno de 8hs..."></textarea>
          </div>
          <div className="form-footer-p">
            <button type="button" className="btn-cancel-modal" onClick={() => setIsModalOpen(false)}>CANCELAR</button>
            <button type="submit" className="btn-save-p">{editingStudy ? 'GUARDAR CAMBIOS' : 'AGREGAR AL CATÁLOGO'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Studies;
